import type { ProductOption, ProductData, ColorOption } from '../types/productTypes';

// Define Shopify product structure based on GraphQL API format
export interface ShopifyProduct {
  id: string;
  handle: string;
  title: string;
  vendor?: string | undefined;
  productType?: string | undefined;

  // GraphQL API format for variants
  variants: {
    edges: Array<{
      node: ShopifyVariant
    }>
  };

  options?: ShopifyOption[] | undefined;

  // GraphQL API format for images
  images: {
    edges: Array<{
      node: ShopifyImage
    }>
  };

  price?: string | undefined;
  tags?: string[] | undefined;
  metafields?: ShopifyMetafield[] | undefined;

  // Allow for additional properties
  [key: string]: unknown;
}

// GraphQL API variant format
interface ShopifyVariant {
  id: string;
  title: string;
  availableForSale: boolean;
  selectedOptions?: Array<{
    name: string;
    value: string;
  }>;
  price?: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  } | null;
  image?: ShopifyImage;
  metafields?: ShopifyMetafield[];
}

export interface ShopifyOption {
  id?: string;
  name: string;
  position?: number;
  values: string[];
}

// GraphQL API image format
interface ShopifyImage {
  id: string;
  url: string;
  altText?: string;
  width: number;
  height: number;
}

interface ShopifyMetafield {
  id?: string;
  key: string;
  namespace: string;
  value: string;
  type?: string;
  reference?: {
    id: string;
    image?: {
      url: string;
      altText?: string;
      width?: number;
      height?: number;
    };
  } | null;
}

/**
 * Converts Shopify products to ProductOption format for the filter dropdown
 */
export function convertToProductOptions(shopifyProducts: ShopifyProduct[]): ProductOption[] {
  // Filter out invalid products and then map
  return shopifyProducts
    .filter(product => product && product.handle && product.title)
    .map(product => {
      // Get the image URL from the first image in GraphQL format
      let imageUrl = '';
      if (product.images?.edges?.[0]?.node?.url) {
        imageUrl = product.images.edges[0].node.url;
      }

      // Generate product URL from handle
      const productUrl = `/products/${product.handle}`;

      // Extract brand from vendor or metafields
      let brand = product.vendor || '';

      // Check for brand metafield if vendor isn't available
      if (!brand || brand === '') {
        brand = getMetafieldValue(product, 'brand');
      }

      // Fallback to inferring brand from product title if still not found
      if (!brand || brand === '') {
        const title = product.title.toLowerCase();
        if (title.includes('iphone')) {
          brand = 'Apple';
        } else if (title.includes('galaxy') || title.includes('samsung')) {
          brand = 'Samsung';
        } else if (title.includes('pixel')) {
          brand = 'Google';
        } else {
          brand = 'Other';
        }
      }

      // Extract prices from variants
      const prices: number[] = [];

      // Process GraphQL variant format
      if (product.variants && product.variants.edges) {
        product.variants.edges.forEach(edge => {
          const variant = edge.node;
          if (variant.price && variant.price.amount) {
            const price = parseFloat(variant.price.amount);
            if (!isNaN(price) && !prices.includes(price)) {
              prices.push(price);
            }
          }
        });
      }

      // Sort prices in ascending order
      prices.sort((a, b) => a - b);

      // Get model name from metafield if available, otherwise use product title
      let modelName = product.title;

      const modelFieldValue = getMetafieldValue(product, 'model');
      if (modelFieldValue) {
        // Handle case where value is a JSON array: ["iPhone 15 Plus"]
        try {
          const parsedValue = JSON.parse(modelFieldValue);
          if (Array.isArray(parsedValue) && parsedValue.length > 0) {
            modelName = parsedValue[0];
          } else {
            modelName = modelFieldValue;
          }
        } catch (e) {
          // If not valid JSON, use the value directly
          modelName = modelFieldValue;
        }
      }

      // Extract metafield values
      const releaseDate = getMetafieldValue(product, 'release_date');
      const navigation = getMetafieldValue(product, 'navigation') || getMetafieldValue(product, 'home button');
      const phoneSize = getMetafieldValue(product, 'phone_size');

      // Create product option with explicit typing
      const productOption: ProductOption = {
        id: product.handle,
        name: modelName,
        brand: brand,
        productType: product.productType || '',
        image: imageUrl,
        url: productUrl,
        releaseDate: releaseDate,
        navigation: navigation,
        phoneSize: phoneSize
      };

      // Only add prices if available
      if (prices.length > 0) {
        productOption.prices = prices;
      }

      return productOption;
    });
}

/**
 * Get unique color options from product variants, with a fallback to tags.
 * This ensures the colors listed are tied to actual, available variants.
 * Each color option will be associated with its corresponding image when available.
 */
// Helper function to process a color option and extract color variants
/**
 * AIDEV-NOTE: Color Option Processing Logic
 * This function extracts color variants from product data with multiple fallback mechanisms:
 * 1. Extract from GraphQL variants
 * 2. Use option values if variants don't have colors
 * 3. Default to a generic color if all else fails
 */
function processColorOption(
  colorOption: ShopifyOption,
  variants: { edges: Array<{ node: ShopifyVariant }> },
  defaultImage: string
): ColorOption[] {
  // Create a map of color variants
  const colorVariants: ColorOption[] = [];

  // Process GraphQL variants format
  if (variants && variants.edges) {
    variants.edges.forEach((edge) => {
      const variant = edge.node;

      // Find the color value from selectedOptions
      if (variant.selectedOptions) {
        const colorSelectedOption = variant.selectedOptions.find(
          option => option && option.name && option.name === colorOption.name
        );

        if (colorSelectedOption && colorSelectedOption.value) {
          const colorName = colorSelectedOption.value;

          // Skip if we already have this color
          const existingColor = colorVariants.find(c => c.name === colorName);
          if (!existingColor) {
            // Find the variant image
            let variantImage = defaultImage;

            // Check if the variant has an image specific to its color
            if (variant.image && variant.image.url) {
              variantImage = variant.image.url;
            }

            colorVariants.push({
              name: colorName,
              color: colorName.toLowerCase().replace(/\s+/g, '-'),
              isActive: colorVariants.length === 0, // First color is active
              image: variantImage
            });
          }
        }
      }
    });
  }

  // If we found color variants, return them
  if (colorVariants.length > 0) {
    return colorVariants;
  }

  // If no color options found from variants, use the option values as fallback
  if (colorOption.values && colorOption.values.length > 0) {
    return colorOption.values.map((value, index) => ({
      name: value,
      color: value.toLowerCase().replace(/\s+/g, '-'),
      isActive: index === 0,
      image: defaultImage
    }));
  }

  // Default fallback if nothing else works
  return [{ name: 'Default', color: 'gray', isActive: true, image: defaultImage }];
}

/**
 * AIDEV-NOTE: Complex Color Extraction Logic
 * This function retrieves color options from a product using multiple source strategies:
 * 1. Metafields - First try to find "color swatches" metafield which might be:
 *    - JSON formatted data with color names and images
 *    - Comma-separated text list of color names
 * 2. Product options - Look for a "color" or "colour" option
 * 3. Color-like values - Look for options with values that resemble colors
 * 4. Tags - Look for tags starting with "color:" or "colour:"
 * 5. Synthetic fallback - Create default colors if all else fails
 * 
 * The function contains significant defensive coding due to inconsistent data formats
 * coming from different Shopify API endpoints.
 */
function getVariantColorOptions(product: ShopifyProduct): ColorOption[] {

  // Extract color options from variants using the color_swatch metafield
  if (product.variants && product.variants.edges && product.variants.edges.length > 0) {
    const colorVariants: ColorOption[] = [];
    const processedColors = new Set<string>(); // To track unique colors

    // Process each variant to find color options
    for (const edge of product.variants.edges) {
      const variant = edge.node;

      // Find the color option in the variant's selectedOptions
      const colorOption = variant.selectedOptions?.find(option =>
        option.name.toLowerCase() === 'color' ||
        option.name.toLowerCase() === 'colour'
      );

      if (colorOption && colorOption.value) {
        const colorName = colorOption.value;

        // Skip if we already processed this color
        if (processedColors.has(colorName.toLowerCase())) {
          continue;
        }

        // Get variant image if available
        let variantImage = '';
        if (variant.image && variant.image.url) {
          variantImage = variant.image.url;
        } else if (product.images?.edges?.[0]?.node?.url) {
          variantImage = product.images.edges[0].node.url;
        }

        // Check for color_swatch metafield on the variant
        let hexCode: string | undefined = undefined;

        if (variant.metafields && Array.isArray(variant.metafields)) {
          const colorSwatchField = variant.metafields.find(
            field => field && field.key && field.namespace &&
              typeof field.key === 'string' &&
              field.key.toLowerCase() === 'color_swatch' &&
              (field.namespace === 'specifications' || field.namespace === 'custom')
          );

          if (colorSwatchField && colorSwatchField.value) {
            // The hexcode is directly in the value
            if (colorSwatchField.value.startsWith('#')) {
              hexCode = colorSwatchField.value;
            }
          }
        }

        // Add the color to our list
        colorVariants.push({
          name: colorName,
          color: colorName.toLowerCase().replace(/\s+/g, '-'),
          hexCode: hexCode,
          isActive: colorVariants.length === 0, // First color is active
          image: variantImage
        });

        // Mark this color as processed
        processedColors.add(colorName.toLowerCase());
      }
    }

    if (colorVariants.length > 0) {
      return colorVariants;
    }
  }

  // Default product image to use when variant-specific image isn't available
  let defaultProductImage = '';
  if (product.images?.edges?.[0]?.node?.url) {
    defaultProductImage = product.images.edges[0].node.url;
  }


  // Handle GraphQL API format
  if (product.options && Array.isArray(product.options) && product.variants && product.variants.edges) {

    // Find which option is for color - check for both "color" and "colour" (UK spelling)
    const colorOption = product.options.find(option =>
      option.name?.toLowerCase() === 'color' ||
      option.name?.toLowerCase() === 'colour'
    );


    // If no color option is found by name, try to find an option with color-like values
    if (!colorOption && product.options.length > 0) {
      const colorLikeValues = ['black', 'white', 'blue', 'red', 'green', 'yellow', 'purple', 'pink', 'orange', 'grey', 'gray', 'silver', 'gold'];

      // Check each option to see if it has color-like values
      for (const option of product.options) {
        if (option.values && Array.isArray(option.values)) {
          const hasColorValues = option.values.some(value =>
            colorLikeValues.includes(value.toLowerCase())
          );

          if (hasColorValues) {
            return processColorOption(option, product.variants, defaultProductImage);
          }
        }
      }
    }

    if (colorOption) {
      return processColorOption(colorOption, product.variants, defaultProductImage);
    }
  }

  // Fall back to tags if no color options are found in variants
  if (product.tags && Array.isArray(product.tags)) {
    const colorTags = product.tags.filter(tag =>
      tag && typeof tag === 'string' && (
        tag.toLowerCase().startsWith('color:') ||
        tag.toLowerCase().startsWith('colour:')
      )
    );

    if (colorTags.length > 0) {
      return colorTags.map((tag, index) => {
        // Safely split the tag to extract the color name
        const parts = tag && typeof tag === 'string' ? tag.split(':') : [];
        const colorName = parts.length > 1 && parts[1] ? parts[1].trim() : 'Default';

        return {
          name: colorName,
          color: colorName.toLowerCase().replace(/\s+/g, '-'),
          isActive: index === 0,
          image: defaultProductImage // Use default image since we don't have variant-specific images
        };
      });
    }
  }

  // If no colors are found, return default options
  // Create synthetic colors for testing
  return [
    { name: 'Black', color: 'black', hexCode: '#333', isActive: true, image: defaultProductImage },
    { name: 'Silver', color: 'silver', hexCode: '#e0e0e0', isActive: false, image: defaultProductImage },
    { name: 'Gold', color: 'gold', hexCode: '#f1c40f', isActive: false, image: defaultProductImage },
    { name: 'Blue', color: 'blue', hexCode: '#a4c6e4', isActive: false, image: defaultProductImage }
  ];
}


/**
 * Helper function to extract metafield value by key and namespace
 */
function getMetafieldValue(product: ShopifyProduct, key: string, namespaces: string[] = ['custom', 'specifications']): string {
  if (!product.metafields || !Array.isArray(product.metafields)) {
    return '';
  }

  const field = product.metafields.find(
    field => field && field.key && field.namespace &&
      typeof field.key === 'string' &&
      field.key.toLowerCase() === key.toLowerCase() &&
      namespaces.includes(field.namespace)
  );

  return field?.value || '';
}

/**
 * Get price information for different storage options
 * AIDEV-NOTE: This function retrieves all Memory variants and their prices.
 * It groups by Memory value and finds the minimum price for each Memory size.
 */
function getPriceOptions(product: ShopifyProduct): ProductData['price'] {

  // Create a map to store memory options and their prices
  const memoryPrices = new Map<string, number[]>();

  // Only proceed if we have both options and variants
  if (product.options && Array.isArray(product.options) &&
    product.variants && product.variants.edges) {

    // Find the Memory option
    const memoryOption = product.options.find(option =>
      option.name === 'Memory'
    );

    if (memoryOption) {

      // Initialize the map with all memory options (even if no variants exist for them)
      if (memoryOption.values && Array.isArray(memoryOption.values)) {
        memoryOption.values.forEach(memory => {
          memoryPrices.set(memory, []);
        });
      }

      // Process all variants to extract Memory values and prices
      product.variants.edges.forEach((edge) => {
        const variant = edge.node;
        const price = variant.price?.amount ? parseFloat(variant.price.amount) : null;

        // Skip variants without a price
        if (price === null) {
          return;
        }

        // Check if this variant has the Memory option
        if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
          // Find the Memory option in this variant's selectedOptions
          const variantMemoryOption = variant.selectedOptions.find(option =>
            option.name === 'Memory'
          );

          if (variantMemoryOption && variantMemoryOption.value) {
            const memoryValue = variantMemoryOption.value;

            // Add this price to our map
            if (!memoryPrices.has(memoryValue)) {
              memoryPrices.set(memoryValue, []);
            }
            memoryPrices.get(memoryValue)?.push(price);
          }
        }
      });
    } else {
      // Try to find any option that might represent storage/memory
      const storageOption = product.options.find(option =>
        option.name && (
          option.name.toLowerCase().includes('storage') ||
          option.name.toLowerCase().includes('capacity') ||
          option.name.toLowerCase().includes('memory') ||
          option.name.toLowerCase().includes('gb')
        )
      );

      if (storageOption) {

        // Initialize the map with all storage options
        if (storageOption.values && Array.isArray(storageOption.values)) {
          storageOption.values.forEach(storage => {
            memoryPrices.set(storage, []);
          });

          // Process variants to find prices for these storage options
          product.variants.edges.forEach(edge => {
            const variant = edge.node;
            const price = variant.price?.amount ? parseFloat(variant.price.amount) : null;

            if (price === null) return;

            if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
              const variantStorageOption = variant.selectedOptions.find(option =>
                option.name.toLowerCase() === storageOption.name.toLowerCase()
              );

              if (variantStorageOption && variantStorageOption.value) {
                const storageValue = variantStorageOption.value;

                if (!memoryPrices.has(storageValue)) {
                  memoryPrices.set(storageValue, []);
                }
                memoryPrices.get(storageValue)?.push(price);
              }
            }
          });
        }
      }
    }
  }

  // Convert the map to the expected format, using the minimum price for each memory size
  const result: Array<{ storage: string, price: string }> = [];

  memoryPrices.forEach((prices, memory) => {
    // Only include memory options that have at least one price
    if (prices.length > 0) {
      result.push({
        storage: memory,
        price: Math.min(...prices).toString()
      });
    }
  });

  // Sort by memory size numerically if possible
  result.sort((a, b) => {
    const aSize = parseInt(a.storage.match(/\d+/)?.[0] || '0');
    const bSize = parseInt(b.storage.match(/\d+/)?.[0] || '0');
    return aSize - bSize;
  });

  if (result.length > 0) {
    return result;
  }

  // If no memory options found, just use the main product price with "Base model"
  const price = typeof product.price === 'string' ? product.price : '0';
  return [{ storage: 'Base model', price }];
}

/**
 * Extract phone size from metafields
 */
function getPhoneSize(product: ShopifyProduct): string {
  return getMetafieldValue(product, 'phone_size');
}

/**
 * Extract screen size from metafields
 */
function getScreenSize(product: ShopifyProduct): string {
  return getMetafieldValue(product, 'display') || 'N/A';
}

/**
 * Extract processor info from metafields
 */
function getProcessor(product: ShopifyProduct): string {
  return getMetafieldValue(product, 'processor') || getMetafieldValue(product, 'model');
}

/**
 * Extract battery life from metafields
 */
function getBatteryLife(product: ShopifyProduct): string {
  return getMetafieldValue(product, 'battery');
}

/**
 * Get camera specifications from metafields
 */
function getCameraSpecs(product: ShopifyProduct): ProductData['cameraSpecs'] {
  if (!product.metafields || !Array.isArray(product.metafields)) {
    return {
      frontCamera: '',
      rearCamera: '',
      slowMotionVideo: '',
      opticalZoom: ''
    };
  }

  const getMetafieldValue = (key: string): string => {
    const field = product.metafields!.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === key &&
        (field.namespace === 'custom' || field.namespace === 'specifications')
    );
    return field?.value || '';
  };

  return {
    frontCamera: getMetafieldValue('front_camera'),
    rearCamera: getMetafieldValue('rear_camera'),
    slowMotionVideo: getMetafieldValue('slow_motion_video'),
    opticalZoom: getMetafieldValue('optical_zoom')
  };
}

/**
 * Determine iOS support information based on device generation and metafields
 */
function getIOSSupport(product: ShopifyProduct): ProductData['iosSupport'] {
  let releaseYear = '';
  let latestIOS = '';
  let yearsUntilEndOfSupport = '';

  if (product.metafields && Array.isArray(product.metafields)) {
    // Check for release_date metafield
    const releaseDateField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'release_date' &&
        (field.namespace === 'custom' || field.namespace === 'specifications')
    );

    if (releaseDateField && releaseDateField.value) {
      // Extract year from the release date value
      const yearMatch = releaseDateField.value.match(/(20\d{2})/);
      if (yearMatch && yearMatch[1]) {
        releaseYear = yearMatch[1];
      }
    }

    // Check for last_os_supported metafield
    const lastOSField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'last_os_supported' &&
        (field.namespace === 'custom' || field.namespace === 'specifications')
    );

    if (lastOSField && lastOSField.value) {
      latestIOS = lastOSField.value;
    }

    // Check for works_until metafield
    const worksUntilField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'works_until' &&
        (field.namespace === 'custom' || field.namespace === 'specifications')
    );

    if (worksUntilField && worksUntilField.value) {
      yearsUntilEndOfSupport = worksUntilField.value;
    }
  }

  return {
    releaseYear,
    latestIOS,
    yearsUntilEndOfSupport
  };
}

/**
 * Determine available storage options
 */
function getStorageOptions(product: ShopifyProduct): string[] {
  // Check for storage in metafields first
  if (product.metafields && Array.isArray(product.metafields)) {
    const storageField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'storage' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    if (storageField && storageField.value) {
      // If storage is specified as a comma-separated list or multiple options
      const storageOptions = storageField.value.split(/[,;|]/).map(s => s.trim());

      if (storageOptions.length > 0) {
        return storageOptions.map(storage => {
          // Try to extract just the GB amount if it includes other text
          const gbMatch = storage.match(/(\d+)\s*GB/i);
          const gbValue = gbMatch ? gbMatch[1] : null;

          // Calculate approximate photos and videos based on storage size
          if (gbValue) {
            const photoCount = Math.round(parseInt(gbValue) * 20000 / 128);
            return `${storage} - ~${photoCount.toLocaleString()} photos and videos`;
          } else {
            // If we can't extract GB value, just return the storage as is
            return storage;
          }
        });
      }
    }
  }

  // If no metafield, check if there's a storage option in product variants
  if (product.options && Array.isArray(product.options)) {
    const storageOption = product.options.find(option =>
      option.name && (
        option.name.toLowerCase().includes('storage') ||
        option.name.toLowerCase().includes('capacity')
      )
    );

    if (storageOption && storageOption.values && Array.isArray(storageOption.values) && storageOption.values.length > 0) {
      return storageOption.values.map(storage =>
        `${storage} - ${storage === '64GB' ? '~10,000' :
          storage === '128GB' ? '~20,000' :
            storage === '256GB' ? '~40,000' :
              storage === '512GB' ? '~80,000' : '~160,000'} photos and videos`
      );
    }
  }

  // Return empty array if no storage options found
  return [];
}

/**
 * Determine navigation type (Home button vs gestures)
 */
function getNavigationType(product: ShopifyProduct): string {
  return getMetafieldValue(product, 'navigation') || getMetafieldValue(product, 'home button');
}

/**
 * Determine charging capabilities
 */
function getChargingOptions(product: ShopifyProduct): string[] {
  const charging: string[] = [];

  // Check metafields for connector type, charging block wattage, and wireless charging
  if (product.metafields && Array.isArray(product.metafields)) {
    // Check for connector type
    const connectorField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'connector' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    // Check for charging block wattage
    const chargingBlockField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'charging_block_wattage' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    // Check for wireless charging support
    const wirelessField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'wireless_charging' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    // Line 1: Connector Cable + Charging Block Wattage Block
    if (connectorField && connectorField.value) {
      let line1 = `${connectorField.value} Cable`;

      if (chargingBlockField && chargingBlockField.value) {
        line1 += ` + ${chargingBlockField.value} Block`;
      }

      charging.push(line1);
    }

    // Line 2: Wireless Charging
    if (wirelessField && wirelessField.value) {
      charging.push(wirelessField.value);
    }
  }

  return charging;
}

function getConnectivityOptions(product: ShopifyProduct): string[] {
  const connectivity: string[] = [];

  // Check metafields for connectivity features
  if (product.metafields && Array.isArray(product.metafields)) {
    // Check for cellular connectivity (3G/4G/5G)
    const cellularField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === '3g_4g_5g' &&
        field.namespace === 'custom'
    );

    // Add cellular connectivity
    if (cellularField && cellularField.value) {
      connectivity.push(cellularField.value);
    }
  }

  return connectivity;
}

function getSimCard(product: ShopifyProduct): string | undefined {
  if (product.metafields && Array.isArray(product.metafields)) {
    const simCardField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'sim_card' &&
        field.namespace === 'custom'
    );

    if (simCardField && simCardField.value) {
      return simCardField.value;
    }
  }

  return undefined;
}

/**
 * Formats a price to display as an integer (no decimal places)
 * @param price - The price value to format
 * @returns The formatted price as a string with no decimal places
 */
export const formatPrice = (price: number | string): string => {
  // Convert to number if it's a string
  const priceNum = typeof price === 'string' ? parseFloat(price) : price;

  // Return the price rounded to a whole number (no decimals)
  return Math.round(priceNum).toString();
};

/**
 * AIDEV-NOTE: Core Data Transformation Function
 * This is the central transformation function for the compare tool. It:
 * 1. Takes raw Shopify product data (which may come from REST or GraphQL API)
 * 2. Extracts and normalizes all product attributes needed for comparison
 * 3. Applies a series of fallback strategies when data is missing
 *
 * Critical challenges:
 * - Inconsistent data sources (metafields, product options, variants, tags)
 * - Need to infer missing data based on product names or other attributes
 * - Must handle both GraphQL and REST API formats with different structures
 * - Must work with incomplete data from older products
 * 
 * AIDEV-QUESTION: Should we refactor this to use a data extraction strategy pattern?
 * Currently we have ~10 product attribute functions that follow similar patterns
 * but with slight variations in fallback strategy.
 */
export function shopifyProductToComparisonData(product: ShopifyProduct): ProductData {

  // Get the image URL from the first GraphQL image
  let imageUrl = '';
  if (product.images?.edges?.[0]?.node?.url) {
    imageUrl = product.images.edges[0].node.url;
  }

  // Process camera images - Check if there are camera metafields first
  let photoUrl = imageUrl;
  let videoUrl = imageUrl;
  const photoUrls: string[] = [];

  if (product.metafields && Array.isArray(product.metafields)) {

    // Look for specific camera photo metafields first (landscape, close up, low light)
    const landscapeField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'landscape' &&
        field.namespace === 'custom'
    );

    const closeUpField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'close_up' &&
        field.namespace === 'custom'
    );

    const lowLightField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'low_light' &&
        field.namespace === 'custom'
    );


    // Import the getMetafieldImageUrl function from cameraGalleryTransformers.ts
    // Since we can't directly import, we'll duplicate the logic here
    function getImageUrlFromMetafield(metafield: any): string | null {
      if (!metafield) return null;

      // Check for MediaImage reference
      if (metafield.reference && metafield.reference.image && metafield.reference.image.url) {
        return metafield.reference.image.url;
      }

      // Check for direct URL in value
      if (metafield.value && typeof metafield.value === 'string' && metafield.value.includes('http')) {
        return metafield.value;
      }

      return null;
    }

    // Add these special metafield photos to our photoUrls array, checking for MediaImage references first
    if (landscapeField) {
      const landscapeUrl = getImageUrlFromMetafield(landscapeField);
      if (landscapeUrl) {
        photoUrls.push(landscapeUrl);
      }
    }

    if (closeUpField) {
      const closeUpUrl = getImageUrlFromMetafield(closeUpField);
      if (closeUpUrl) {
        photoUrls.push(closeUpUrl);
      }
    }

    if (lowLightField) {
      const lowLightUrl = getImageUrlFromMetafield(lowLightField);
      if (lowLightUrl) {
        photoUrls.push(lowLightUrl);
      }
    }

    // Check for front or rear camera images
    const frontCameraField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'front_camera' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    const rearCameraField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'rear_camera' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    // Check for general camera field that might contain image URLs
    const cameraField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'camera' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    // Try to extract photo and video URLs from metafields
    if (rearCameraField && rearCameraField.value && rearCameraField.value.includes('http')) {
      // If value contains URL, use it for photo
      const urlMatch = rearCameraField.value.match(/(https?:\/\/[^\s]+)/);
      if (urlMatch) {
        photoUrl = urlMatch[0];
        // If we don't already have this URL in photoUrls, add it
        if (!photoUrls.includes(photoUrl)) {
          photoUrls.push(photoUrl);
        }
      }
    }

    if (frontCameraField && frontCameraField.value && frontCameraField.value.includes('http')) {
      // If value contains URL, use it for video
      const urlMatch = frontCameraField.value.match(/(https?:\/\/[^\s]+)/);
      if (urlMatch) videoUrl = urlMatch[0];
    }

    if (cameraField && cameraField.value) {
      try {
        // Try to parse as JSON
        const cameraData = JSON.parse(cameraField.value);
        if (cameraData.photo) {
          photoUrl = cameraData.photo;
          // If we don't already have this URL in photoUrls, add it
          if (!photoUrls.includes(photoUrl)) {
            photoUrls.push(photoUrl);
          }
        }
        if (cameraData.video) videoUrl = cameraData.video;

        // Check if camera data contains an array of photos
        if (Array.isArray(cameraData.photos)) {
          cameraData.photos.forEach((url: string) => {
            if (url && !photoUrls.includes(url)) {
              photoUrls.push(url);
            }
          });
        }
      } catch (e) {
        // If not JSON, check if it contains URLs
        const urlMatches = cameraField.value.match(/(https?:\/\/[^\s]+)/g);
        if (urlMatches && urlMatches.length > 0) {
          photoUrl = urlMatches[0] || photoUrl;
          // Add all URL matches to photoUrls if they're not already there
          urlMatches.forEach(url => {
            if (!photoUrls.includes(url)) {
              photoUrls.push(url);
            }
          });

          if (urlMatches.length > 1) videoUrl = urlMatches[1] || videoUrl;
        }
      }
    }
  }


  // If we still don't have any photos in photoUrls, use product images as fallback
  if (photoUrls.length === 0) {
    // Try to use up to 3 product images for the photo slider
    if (product.images?.edges) {
      const edges = product.images.edges;

      for (let i = 1; i < Math.min(4, edges.length); i++) {
        const url = edges[i]?.node?.url;
        if (url) {
          photoUrls.push(url);
        }
      }
    }
  }

  // Ensure photoUrl is set
  if (photoUrls.length > 0) {
    photoUrl = photoUrls[0] || '';
  } else if (photoUrl === imageUrl && product.images?.edges?.[1]?.node?.url) {
    photoUrl = product.images.edges[1].node.url;
    photoUrls.push(photoUrl);
  }

  // Make sure photoUrl is never undefined
  photoUrl = photoUrl || '';

  // Fallback for video URL
  if (videoUrl === imageUrl && product.images?.edges?.[2]?.node?.url) {
    videoUrl = product.images.edges[2].node.url;
  }

  // Get raw SD card support value from metafields
  let sdCardCompatible = 'No'; // Default value
  if (product.metafields && Array.isArray(product.metafields)) {
    const sdCardField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'microsd_support' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    if (sdCardField && sdCardField.value) {
      sdCardCompatible = sdCardField.value;
    }
  }

  // Get Apple Watch support values from metafields
  let appleWatchSupport = '';
  let appleWatchSESupport = '';
  let appleWatchUltraSupport = '';

  if (product.metafields && Array.isArray(product.metafields)) {
    // Look for the apple_watch_compatible metafield
    const watchSupportField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'apple_watch_compatible' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    if (watchSupportField && watchSupportField.value) {
      appleWatchSupport = watchSupportField.value;
    }

    // Look for the apple_watch_se_compatible metafield
    const watchSESupportField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'apple_watch_se_compatible' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    if (watchSESupportField && watchSESupportField.value) {
      appleWatchSESupport = watchSESupportField.value;
    }

    // Look for the apple_watch_ultra_compatible metafield
    const watchUltraSupportField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'apple_watch_ultra_compatible' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    if (watchUltraSupportField && watchUltraSupportField.value) {
      appleWatchUltraSupport = watchUltraSupportField.value;
    }
  }

  // Determine ease of use score from metafields
  let easeOfUseScore = '';

  if (product.metafields && Array.isArray(product.metafields)) {
    const easeOfUseField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        (field.key.toLowerCase().includes('ease') || field.key.toLowerCase().includes('usability')) &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    if (easeOfUseField && easeOfUseField.value) {
      // If the value is just a number, format it as X/5
      if (/^\d+$/.test(easeOfUseField.value.trim())) {
        easeOfUseScore = `${easeOfUseField.value.trim()}/5`;
      } else {
        easeOfUseScore = easeOfUseField.value;
      }
    }
  }

  // Generate product URL from handle
  const productUrl = `/products/${product.handle}`;

  // Get model name from metafield if available, otherwise use product title
  let modelName = product.title;

  if (product.metafields && Array.isArray(product.metafields)) {
    const modelField = product.metafields.find(
      field => field && field.key && field.namespace &&
        typeof field.key === 'string' &&
        field.key.toLowerCase() === 'model' &&
        (field.namespace === 'specifications' || field.namespace === 'custom')
    );

    if (modelField && modelField.value) {
      // Handle case where value is a JSON array: ["iPhone 15 Plus"]
      try {
        const parsedValue = JSON.parse(modelField.value);
        if (Array.isArray(parsedValue) && parsedValue.length > 0) {
          modelName = parsedValue[0];
        } else {
          modelName = modelField.value;
        }
      } catch (e) {
        // If not valid JSON, use the value directly
        modelName = modelField.value;
      }
    }
  }

  return {
    name: modelName,
    image: imageUrl,
    url: productUrl,
    colorOptions: getVariantColorOptions(product),
    price: getPriceOptions(product),
    easeOfUseScore,
    navigation: getNavigationType(product),
    iosSupport: getIOSSupport(product),
    phoneSize: getPhoneSize(product),
    screenSize: getScreenSize(product),
    batteryLife: getBatteryLife(product),
    camera: {
      photoUrl,
      photoUrls, // Add the array of multiple photo URLs for the slider
      videoUrl,
      // Use external video URL directly - no local files in folders
      videoSrc: "https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4" // Direct external link
    },
    cameraSpecs: getCameraSpecs(product),
    processor: getProcessor(product),
    storage: getStorageOptions(product),
    sdCardCompatible,
    appleWatchSupport,
    appleWatchSESupport,
    appleWatchUltraSupport,
    charging: getChargingOptions(product),
    connectivity: getConnectivityOptions(product),
    simCard: getSimCard(product)
  };
}
